import { useState } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CallButton } from "./CallButton";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-ink outline-none transition-colors focus:border-brand";

// fields: [{ name, label, type, required, options, full }]
export const LeadForm = ({ fields, source, submitLabel = "Send", note, testId = "lead-form", columns = 2 }) => {
  const [values, setValues] = useState({});
  const [state, setState] = useState({ busy: false, done: false, error: "" });
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: false, error: "" });
    const { name = "", phone = "", email = "", message = "", ...details } = values;
    try {
      await api.post("/leads", { name, phone, email, message, source, details });
      setState({ busy: false, done: true, error: "" });
      setValues({});
    } catch (err) {
      setState({ busy: false, done: false, error: formatApiError(err.response?.data?.detail) });
    }
  };

  if (state.done) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid={`${testId}-success`}>
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
        <p className="mt-3 font-display text-lg font-bold text-ink">Thanks — request received.</p>
        <p className="mt-1 text-sm text-slate-600">A travel specialist will contact you shortly. Need it faster?</p>
        <CallButton testId={`${testId}-success-call`} className="mt-4" showNumber />
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={testId} className={`grid gap-4 ${columns === 2 ? "sm:grid-cols-2" : ""}`}>
      {fields.map((f) => (
        <label key={f.name} className={`block ${f.full ? "sm:col-span-2" : ""}`}>
          <span className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-600">{f.label}{f.required && " *"}</span>
          {f.options ? (
            <select required={f.required} value={values[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} className={inputCls} data-testid={`${testId}-${f.name}`}>
              <option value="" disabled>Select</option>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : f.type === "textarea" ? (
            <textarea rows={4} required={f.required} value={values[f.name] ?? ""} placeholder={f.placeholder} onChange={(e) => set(f.name, e.target.value)} className={inputCls} data-testid={`${testId}-${f.name}`} />
          ) : (
            <input type={f.type || "text"} required={f.required} value={values[f.name] ?? ""} placeholder={f.placeholder} onChange={(e) => set(f.name, e.target.value)} className={inputCls} data-testid={`${testId}-${f.name}`} />
          )}
        </label>
      ))}
      {state.error && <p className="text-sm text-red-600 sm:col-span-2" data-testid={`${testId}-error`}>{state.error}</p>}
      <button type="submit" disabled={state.busy} data-testid={`${testId}-submit`}
        className="flex items-center justify-center gap-2 rounded-xl bg-ink py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand disabled:opacity-60 sm:col-span-2">
        {state.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {submitLabel}
      </button>
      {note && <p className="text-center text-xs text-slate-500 sm:col-span-2">{note}</p>}
    </form>
  );
};
